/**
 * Constants + small formatters used by BriefingPage and the
 * BriefingRow subcomponent. Extracted so the page file reads as
 * data-fetch + composition logic, not a 600-line monolith.
 */
import { Target } from 'lucide-react';
import { ITEM_TYPE_CONFIG } from '../../components/commitments/commitmentConstants';

// How often to silently re-poll the briefing endpoint while the tab is
// open and visible. 60s strikes the right balance for a planner app —
// frequent enough to feel live, sparse enough to not hammer the API.
export const POLL_INTERVAL_MS = 60_000;

// Icon/label single-source: commitmentConstants.ITEM_TYPE_CONFIG. The briefing
// only adds the deep-link route + the `commitment` row type
// (BRIEFING-ITEM-TYPE-META-DRIFT-1 / CONFIG-DRIFT-SWEEP-1).
const ITEM_ROUTES = { commitment: '/commitments', task: '/tasks', payment: '/payments', call: '/calls', reading: '/reading' };

export const ITEM_TYPE_META = Object.fromEntries(
  Object.entries(ITEM_ROUTES).map(([type, route]) => [type, {
    icon: ITEM_TYPE_CONFIG[type]?.icon || Target,
    label: ITEM_TYPE_CONFIG[type]?.label || 'Commitment',
    route,
  }]),
);

// Window presets exposed by the tab switcher. Each option carries the
// `hours` passed to the briefing API + the editorial copy that lights
// up in the page header subtitle, so the same page reads with a
// different temperature based on which slice the user picks.
export const WINDOW_PRESETS = [
  { hours: 12, label: 'Next 12h', subtitle: 'BITING IN THE NEXT 12 HOURS' },
  { hours: 24, label: 'Next 24h', subtitle: 'BREWING IN THE NEXT 24 HOURS' },
  { hours: 48, label: 'Next 48h', subtitle: 'LINING UP IN THE NEXT 48 HOURS' },
];

export function presetFromParam(raw) {
  const n = Number(raw);
  return WINDOW_PRESETS.find((p) => p.hours === n) || WINDOW_PRESETS[0];
}

const CURRENCY_PREFIX = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

export function formatAmount(amount, currency) {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  const prefix = CURRENCY_PREFIX[currency] || (currency ? `${currency} ` : '');
  if (Math.abs(amount) >= 1000) {
    return `${prefix}${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  }
  return `${prefix}${Math.round(amount)}`;
}

export function formatHoursUntil(hours) {
  const h = Math.max(0, Math.round(hours));
  if (h < 1) return '<1 hour';
  if (h < 24) return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  const days = Math.floor(h / 24);
  const rem = h - days * 24;
  if (rem === 0) return `${days} ${days === 1 ? 'day' : 'days'}`;
  return `${days} day${days === 1 ? '' : 's'} ${rem}h`;
}

export function tierOf(hoursUntil) {
  if (typeof hoursUntil !== 'number' || !Number.isFinite(hoursUntil)) return 'later';
  if (hoursUntil <= 12) return 'urgent';
  if (hoursUntil <= 24) return 'soon';
  return 'later';
}

// Format an absolute timestamp into a soft "Updated Xs ago" stamp. We
// intentionally re-render this every 10s via a tick state so the value
// stays honest without re-fetching the data.
export function formatUpdatedAgo(timestamp, now) {
  if (!timestamp) return '';
  const diffSec = Math.max(0, Math.round((now - timestamp) / 1000));
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `Updated ${diffSec}s ago`;
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `Updated ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `Updated ${hrs}h ago`;
}

// Group threshold: only worth splitting into business sections once the
// user has 3+ distinct workspaces represented in the briefing — otherwise
// the headers add noise instead of clarity.
const GROUP_THRESHOLD = 3;

/**
 * Slice the items list into business-grouped sections OR return null
 * if the user has fewer than `GROUP_THRESHOLD` distinct workspaces.
 * We preserve the inbound order inside each group so the urgency sort
 * from the backend survives.
 */
export function groupByBusiness(items) {
  const workspaces = new Set(items.map((it) => it.businessName || 'Personal'));
  if (workspaces.size < GROUP_THRESHOLD) return null;
  const groups = new Map();
  for (const it of items) {
    const key = it.businessName || 'Personal';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  }
  // Order groups by the soonest item's hoursUntil — the workspace
  // that's burning the hottest comes first.
  return Array.from(groups.entries())
    .map(([name, rows]) => ({ name, rows, soonest: rows[0]?.hoursUntil ?? Infinity }))
    .sort((a, b) => a.soonest - b.soonest);
}
